import { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PiggyBank, Plus, Edit2, Trash2, DollarSign, Wallet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Suggested savings types for quick entry
const SUGGESTED_TYPES = [
  "HSA - Rick",
  "HSA - Nazli",
  "Brokerage",
  "529 Plan",
  "Roth IRA",
  "Emergency Fund",
];

export default function OtherSavings() {
  const [year] = useState(2025);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formNotes, setFormNotes] = useState("");

  useEffect(() => {
    fetchData();
  }, [year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/other-savings?year=${year}`);
      setSummary(res.data);
    } catch (error) {
      toast.error("Failed to load savings data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormName("");
    setFormAmount("");
    setFormNotes("");
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormName(item.name);
    setFormAmount(String(item.amount));
    setFormNotes(item.notes || "");
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this savings entry?")) return;
    try {
      await api.delete(`/other-savings/${id}`);
      toast.success("Entry deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName || !formAmount) {
      toast.error("Please fill in name and amount");
      return;
    }

    try {
      await api.post("/other-savings", {
        name: formName,
        year,
        amount: parseFloat(formAmount),
        notes: formNotes || null,
      });
      
      toast.success(editingItem ? "Entry updated!" : "Entry added!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save entry");
    }
  };

  const handleQuickAdd = (type) => {
    setFormName(type);
    setDialogOpen(true);
  };

  // Get types that haven't been added yet
  const existingNames = new Set((summary?.items || []).map(i => i.name));
  const availableSuggestions = SUGGESTED_TYPES.filter(t => !existingNames.has(t));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
            Other Savings
          </h1>
          <p className="text-slate-500 mt-1">
            Track HSA, brokerage, and other savings not in YNAB
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-slate-900 hover:bg-slate-800" data-testid="add-savings-btn">
              <Plus className="h-4 w-4" />
              Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                {editingItem ? "Edit Savings Entry" : "Add Savings Entry"}
              </DialogTitle>
              <DialogDescription>
                Track savings contributions outside of your regular budget
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name / Type</Label>
                <Input
                  placeholder="e.g., HSA - Rick, Brokerage"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  data-testid="savings-name-input"
                />
                {/* Quick suggestions if adding new */}
                {!editingItem && availableSuggestions.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {availableSuggestions.slice(0, 4).map(suggestion => (
                      <Badge 
                        key={suggestion}
                        variant="secondary" 
                        className="cursor-pointer hover:bg-slate-200 text-xs"
                        onClick={() => setFormName(suggestion)}
                      >
                        {suggestion}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Total Amount for {year}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    className="pl-7"
                    data-testid="savings-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Enter the total amount contributed/saved for the year
                </p>
              </div>

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Any additional details..."
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                  data-testid="savings-notes-input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="save-savings-btn">
                  {editingItem ? "Update" : "Add"} Entry
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-violet-600 font-medium">Total Other Savings ({year})</p>
                <p className="text-3xl font-bold font-mono text-violet-700 mt-1">
                  {formatCurrency(summary?.total || 0)}
                </p>
                <p className="text-xs text-violet-600 mt-1">
                  {summary?.items?.length || 0} entries
                </p>
              </div>
              <Wallet className="h-10 w-10 text-violet-400" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">What to Track Here</p>
                <p className="text-sm text-slate-500 mt-2">
                  HSA contributions, brokerage deposits, 529 plans, or any other savings not tracked in your YNAB budget
                </p>
              </div>
              <PiggyBank className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Add Suggestions */}
      {availableSuggestions.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-600 mb-3">Quick add:</p>
            <div className="flex flex-wrap gap-2">
              {availableSuggestions.map(type => (
                <Button
                  key={type}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickAdd(type)}
                  className="gap-1"
                >
                  <Plus className="h-3 w-3" />
                  {type}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Savings Entries List */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-lg">
            Savings Entries for {year}
          </CardTitle>
          <CardDescription>
            These amounts are added to your total net wealth on the Dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(summary?.items || []).length > 0 ? (
            <div className="space-y-3">
              {(summary?.items || []).map((item) => (
                <div
                  key={item.id}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between"
                  data-testid={`savings-item-${item.id}`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{item.name}</span>
                    </div>
                    {item.notes && (
                      <p className="text-xs text-slate-500">{item.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-xl font-bold font-mono text-violet-700">
                      {formatCurrency(item.amount)}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                        data-testid={`edit-savings-${item.id}`}
                      >
                        <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-savings-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No savings entries yet</h3>
              <p className="text-slate-500 mb-4">Add HSA, brokerage, or other savings to track your total wealth</p>
              <Button onClick={() => setDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add First Entry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
